/**
 * Транспорт до сервиса.
 *
 * Форма взята у соседа (`/claude/claude/supply/web/src/lib/api.ts`), а РАЗБОР
 * здесь другой, и это не небрежность копирования. Конверты у сервисов разные:
 *
 *     supply   { error: [ {code, message} ], results: {...} }
 *     wms      голый объект ответа; ошибка — {"error": "unauthorized"} + код
 *
 * Опора: app/api/routes/auth.py:214 и app/api/main.py:99. Дословная копия
 * разбора искала бы `results` там, где его нет, и каждый ответ читался бы как
 * пустой.
 */

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    // Сессия живёт в httpOnly-cookie, поэтому запрос обязан её нести.
    credentials: "same-origin",
  });

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Шлюз может ответить HTML-страницей ошибки. Это не повод падать
    // непонятным SyntaxError вместо честного статуса.
    body = null;
  }

  if (!response.ok) {
    const code =
      body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : `http_${response.status}`;
    throw new ApiError(response.status, code);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => call<T>(path),
  post: <T>(path: string, data: unknown) =>
    call<T>(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    }),
  delete: <T>(path: string, data: unknown) =>
    call<T>(path, {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(data),
    }),
};

/** Собрать query-строку, выбросив пустые значения: они превращают ссылку в мусор. */
export function query(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === false
    )
      continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
